import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useStyles } from '../checkoutDrawer.styles';

function StateSelect ({ value, options, onChange, onBlur, error, disabled }) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const containerRef = useRef(null);
    const listRef = useRef(null);

    const selected = options.find((o) => o.value === value) || null;

    const close = useCallback(() => setOpen(false), []);

    const select = useCallback((opt) => {
        onChange(opt.value);
        close();
    }, [onChange, close]);

    // Suppress parent scroll while open — same pattern as CountrySelect
    useLayoutEffect(() => {
        if (!open) return undefined;
        let scrollParent = containerRef.current?.parentElement;
        while (scrollParent) {
            const { overflowY } = window.getComputedStyle(scrollParent);
            if (overflowY === 'auto' || overflowY === 'scroll') break;
            scrollParent = scrollParent.parentElement;
        }
        // Flip the panel upward against the scroll container's visible bottom
        // (which sits above the fixed footer), not the window — otherwise the
        // list opens down behind the footer and gets clipped. Decided here,
        // before the overflow is mutated below.
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const boundaryBottom = scrollParent
                ? scrollParent.getBoundingClientRect().bottom
                : window.innerHeight;
            setOpenUpward(boundaryBottom - rect.bottom < 240);
        }
        if (scrollParent) scrollParent.style.overflowY = 'hidden';
        return () => { if (scrollParent) scrollParent.style.overflowY = ''; };
    }, [open]);

    // Position panel and dismiss on click-outside / Escape
    useEffect(() => {
        if (!open) return undefined;
        // Scroll selected item into view
        if (listRef.current && value) {
            const node = listRef.current.querySelector('[aria-selected="true"]');
            if (node) {
                listRef.current.scrollTop = node.offsetTop - (listRef.current.clientHeight - node.offsetHeight) / 2;
            }
        }
        function handlePointer (e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                close();
            }
        }
        function handleKeyDown (e) {
            if (e.key === 'Escape') close();
        }
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, value, close]);

    const listboxId = 'co-state-listbox';

    return (
        <div className={classes.countrySelect} ref={containerRef}>
            <button
                type="button"
                className={`${classes.countryTrigger} ${error ? classes.inputError : ''} ${open ? classes.countryTriggerOpen : ''} ${disabled ? classes.countryTriggerDisabled : ''}`}
                onClick={() => { if (!disabled) setOpen((o) => !o); }}
                onBlur={onBlur}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label="State / Province"
                aria-disabled={disabled}
            >
                <span className={selected ? classes.countryValue : classes.countryPlaceholder}>
                    {selected ? selected.label : 'State / Province'}
                </span>
            </button>

            {open && (
                <div
                    className={`${classes.countryPanel} ${openUpward ? '' : classes.countryPanelBelow}`}
                    role="dialog"
                    aria-label="Select a state"
                >
                    <ul className={classes.countryList} ref={listRef} role="listbox" id={listboxId} aria-label="States">
                        {options.length === 0 ? (
                            <li className={classes.countryEmpty}>No states available</li>
                        ) : (
                            options.map((opt) => {
                                const isSelected = opt.value === value;
                                return (
                                    <li
                                        key={opt.value}
                                        role="option"
                                        tabIndex={0}
                                        aria-selected={isSelected}
                                        className={`${classes.countryOption} ${isSelected ? classes.countryOptionSelected : ''}`}
                                        onClick={() => select(opt)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                select(opt);
                                            }
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {isSelected && <span className={classes.countryCheck} aria-hidden="true">✓</span>}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

StateSelect.propTypes = {
    value: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default StateSelect;
